import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  TrendingUp,
  Wallet,
  ArrowDownToLine,
  ArrowUpFromLine,
  Calendar,
  Download,
  RefreshCcw,
  ChevronDown,
  DollarSign,
  Percent,
  Users,
  CreditCard,
  PieChart,
  BarChart3,
  LineChart,
} from 'lucide-react'
import { cn } from '@/lib/utils'

type DateRange = 'today' | 'yesterday' | 'week' | 'month' | 'quarter' | 'year' | 'custom'

export function FinancialReportPage() {
  const [dateRange, setDateRange] = useState<DateRange>('today')

  const dateRangeLabels: Record<DateRange, string> = {
    today: 'วันนี้',
    yesterday: 'เมื่อวาน',
    week: '7 วันที่ผ่านมา',
    month: 'เดือนนี้',
    quarter: 'ไตรมาสนี้',
    year: 'ปีนี้',
    custom: 'กำหนดเอง',
  }

  // Mock data
  const summaryStats = {
    totalDeposit: 15250000,
    totalWithdraw: 12850000,
    netProfit: 2400000,
    profitMargin: 15.7,
    totalTransactions: 1856,
    avgTransaction: 8215,
    activeMembers: 2345,
    newMembers: 156,
  }

  const depositByMethod = [
    { method: 'โอนผ่านธนาคาร', amount: 8500000, percent: 55.7, count: 450 },
    { method: 'TrueMoney Wallet', amount: 3200000, percent: 21.0, count: 320 },
    { method: 'PromptPay', amount: 2550000, percent: 16.7, count: 280 },
    { method: 'บัตรเครดิต', amount: 1000000, percent: 6.6, count: 85 },
  ]

  const hourlyStats = [
    { hour: '00:00', deposit: 125000, withdraw: 85000 },
    { hour: '02:00', deposit: 98000, withdraw: 72000 },
    { hour: '04:00', deposit: 65000, withdraw: 45000 },
    { hour: '06:00', deposit: 85000, withdraw: 62000 },
    { hour: '08:00', deposit: 220000, withdraw: 180000 },
    { hour: '10:00', deposit: 380000, withdraw: 290000 },
    { hour: '12:00', deposit: 520000, withdraw: 420000 },
    { hour: '14:00', deposit: 480000, withdraw: 380000 },
    { hour: '16:00', deposit: 550000, withdraw: 450000 },
    { hour: '18:00', deposit: 680000, withdraw: 520000 },
    { hour: '20:00', deposit: 850000, withdraw: 680000 },
    { hour: '22:00', deposit: 720000, withdraw: 580000 },
  ]

  const topDepositors = [
    { rank: 1, username: 'vip_player01', amount: 850000, transactions: 25 },
    { rank: 2, username: 'high_roller', amount: 720000, transactions: 18 },
    { rank: 3, username: 'lucky_star', amount: 650000, transactions: 32 },
    { rank: 4, username: 'gold_member', amount: 580000, transactions: 22 },
    { rank: 5, username: 'diamond_vip', amount: 520000, transactions: 15 },
  ]

  const bankPerformance = [
    { bank: 'กสิกรไทย', deposit: 5200000, withdraw: 4100000, balance: 2500000, utilization: 85 },
    { bank: 'ไทยพาณิชย์', deposit: 3800000, withdraw: 3200000, balance: 1800000, utilization: 72 },
    { bank: 'กรุงเทพ', deposit: 2800000, withdraw: 2400000, balance: 1200000, utilization: 45 },
    { bank: 'กรุงไทย', deposit: 2200000, withdraw: 1950000, balance: 950000, utilization: 38 },
  ]

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('th-TH').format(num)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">รายงานการเงิน</h1>
          <p className="text-muted-foreground">สรุปภาพรวมทางการเงินของระบบ</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Date Range Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Calendar className="h-4 w-4" />
                {dateRangeLabels[dateRange]}
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {Object.entries(dateRangeLabels).map(([key, label]) => (
                <DropdownMenuItem
                  key={key}
                  onClick={() => setDateRange(key as DateRange)}
                  className={cn(dateRange === key && "bg-accent")}
                >
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button variant="outline" size="icon">
            <RefreshCcw className="h-4 w-4" />
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Download className="h-4 w-4" />
                ส่งออก
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>📊 Excel (.xlsx)</DropdownMenuItem>
              <DropdownMenuItem>📄 PDF</DropdownMenuItem>
              <DropdownMenuItem>📑 CSV</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-lg bg-win/10">
                <ArrowDownToLine className="h-6 w-6 text-win" />
              </div>
              <Badge variant="secondary" className="bg-win/10 text-win border-0 gap-1">
                <TrendingUp className="h-3 w-3" />
                +12.5%
              </Badge>
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">ยอดฝากรวม</p>
              <p className="text-2xl font-bold">{formatCurrency(summaryStats.totalDeposit)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-lg bg-orange-500/10">
                <ArrowUpFromLine className="h-6 w-6 text-orange-500" />
              </div>
              <Badge variant="secondary" className="bg-orange-500/10 text-orange-500 border-0 gap-1">
                <TrendingUp className="h-3 w-3" />
                +8.3%
              </Badge>
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">ยอดถอนรวม</p>
              <p className="text-2xl font-bold">{formatCurrency(summaryStats.totalWithdraw)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-lg bg-primary/10">
                <Wallet className="h-6 w-6 text-primary" />
              </div>
              <Badge variant="secondary" className="bg-win/10 text-win border-0 gap-1">
                <TrendingUp className="h-3 w-3" />
                +25.3%
              </Badge>
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">กำไรสุทธิ</p>
              <p className={cn(
                "text-2xl font-bold",
                summaryStats.netProfit >= 0 ? "text-win" : "text-rose-600"
              )}>{formatCurrency(summaryStats.netProfit)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-lg bg-blue-500/10">
                <Percent className="h-6 w-6 text-blue-500" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">อัตรากำไร</p>
              <p className="text-2xl font-bold">{summaryStats.profitMargin}%</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="bg-muted/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CreditCard className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">ธุรกรรมทั้งหมด</p>
                <p className="text-lg font-bold">{formatNumber(summaryStats.totalTransactions)} รายการ</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-muted/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <DollarSign className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">เฉลี่ยต่อรายการ</p>
                <p className="text-lg font-bold">{formatCurrency(summaryStats.avgTransaction)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-muted/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">สมาชิกใช้งาน</p>
                <p className="text-lg font-bold">{formatNumber(summaryStats.activeMembers)} คน</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-muted/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-win" />
              <div>
                <p className="text-xs text-muted-foreground">สมาชิกใหม่</p>
                <p className="text-lg font-bold text-win">+{formatNumber(summaryStats.newMembers)} คน</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Deposit by Method */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              ช่องทางการฝากเงิน
            </CardTitle>
            <CardDescription>สัดส่วนยอดฝากแยกตามช่องทาง</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {depositByMethod.map((item, idx) => (
                <div key={idx} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{item.method}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-muted-foreground">{item.count} รายการ</span>
                      <span className="font-semibold">{formatCurrency(item.amount)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Progress 
                      value={item.percent} 
                      className="h-2 flex-1"
                      indicatorClassName={cn(
                        idx === 0 && "bg-win",
                        idx === 1 && "bg-blue-500",
                        idx === 2 && "bg-purple-500",
                        idx === 3 && "bg-orange-500"
                      )}
                    />
                    <span className="text-xs text-muted-foreground w-12 text-right">{item.percent}%</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Hourly Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              ยอดรายชั่วโมง
            </CardTitle>
            <CardDescription>เปรียบเทียบยอดฝาก-ถอนรายชั่วโมง</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {hourlyStats.filter((_, i) => i % 2 === 0).map((item, idx) => {
                const maxAmount = Math.max(...hourlyStats.map(h => Math.max(h.deposit, h.withdraw)))
                return (
                  <div key={idx} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground w-12">{item.hour}</span>
                      <div className="flex items-center gap-4">
                        <span className="text-win">{formatCurrency(item.deposit)}</span>
                        <span className="text-orange-500">{formatCurrency(item.withdraw)}</span>
                      </div>
                    </div>
                    <div className="flex gap-1 h-2">
                      <div 
                        className="bg-win/60 rounded-sm"
                        style={{ width: `${(item.deposit / maxAmount) * 100}%` }}
                      />
                      <div 
                        className="bg-orange-500/60 rounded-sm"
                        style={{ width: `${(item.withdraw / maxAmount) * 100}%` }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-sm bg-win/60" />
                <span className="text-xs text-muted-foreground">ฝาก</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-sm bg-orange-500/60" />
                <span className="text-xs text-muted-foreground">ถอน</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Section */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Top Depositors */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Top 5 ผู้ฝากสูงสุด
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {topDepositors.map((user) => (
                <div key={user.rank} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "h-8 w-8 rounded-full flex items-center justify-center font-bold text-sm",
                      user.rank === 1 && "bg-yellow-500/20 text-yellow-500",
                      user.rank === 2 && "bg-gray-400/20 text-gray-400",
                      user.rank === 3 && "bg-orange-600/20 text-orange-600",
                      user.rank > 3 && "bg-muted text-muted-foreground"
                    )}>
                      {user.rank}
                    </div>
                    <div>
                      <p className="font-medium">{user.username}</p>
                      <p className="text-xs text-muted-foreground">{user.transactions} รายการ</p>
                    </div>
                  </div>
                  <span className="font-semibold text-win">{formatCurrency(user.amount)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Bank Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LineChart className="h-5 w-5" />
              ประสิทธิภาพบัญชีธนาคาร
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {bankPerformance.map((bank, idx) => (
                <div key={idx} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{bank.bank}</span>
                    <span className="text-sm text-muted-foreground">
                      คงเหลือ: <span className="font-semibold text-foreground">{formatCurrency(bank.balance)}</span>
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">ฝาก:</span>
                      <span className="text-win">{formatCurrency(bank.deposit)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">ถอน:</span>
                      <span className="text-orange-500">{formatCurrency(bank.withdraw)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Progress 
                      value={bank.utilization} 
                      className="h-1.5 flex-1"
                      indicatorClassName={cn(
                        bank.utilization >= 80 && "bg-destructive",
                        bank.utilization >= 60 && bank.utilization < 80 && "bg-warning",
                        bank.utilization < 60 && "bg-win"
                      )}
                    />
                    <span className="text-xs text-muted-foreground w-10">{bank.utilization}%</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}








